// vg-binding: P7.D-01
// vg-binding: P7.D-06
// vg-binding: P7.D-120
// vg-binding: P7.D-132
// vg-binding: P7.D-134
// vg-binding: P7.D-142
//
// apps/vendor/src/components/VendorSidebar.tsx
//
// Phase 7 Wave 2 Task 06 — vendor SPA sidebar (expanded shell).
//
//  + DESIGN.md §8 the vendor sidebar puts the avatar+name+email
// block at the TOP (not bottom, that's the merchant variant). Phase 1
// shipped a 5-item flat nav; Phase 7 expands to the canonical 9-item shell
// from the vendor_dashboard design reference:
//
//   Dashboard, Orders, Products, Inventory, Earnings, Payouts, Analytics,
//   Notifications, Settings.
//
// Items NOT yet wired (Earnings / Payouts / Analytics) keep the "Soon"
// pill from Phase 1; the four newly-introduced items (Orders, Products,
// Inventory, Notifications) ship enabled — Wave 6 / Wave 8 / Wave 9 /
// Wave 13 land the real pages, this wave just installs placeholders so
// the routes don't 404. Settings is unchanged.
//
// Test IDs (PLAN/task-06 <test_ids>):
//   - sidebar-dashboard / sidebar-orders / sidebar-products /
//     sidebar-inventory / sidebar-earnings / sidebar-payouts /
//     sidebar-analytics / sidebar-notifications / sidebar-settings.
//   These are surfaced as `data-testid` on each anchor so the upcoming
//   Playwright suite can locator-by-test-id without coupling to text
//   labels (which translate vi/en).

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Sidebar, cn, sidebarNavItemActiveClassName, useSidebar } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { useVendorSupportUnread } from '../features/support/supportRealtime.js';

function PrintwayLogo(): JSX.Element {
  return (
    <div className="flex items-center">
      <img
        src="/printway-logo.avif"
        alt="Printway"
        className="h-8 w-auto shrink-0"
      />
    </div>
  );
}

interface NavLinkProps {
  to: string;
  label: string;
  icon: JSX.Element;
  active?: boolean;
  disabled?: boolean;
  badge?: string;
  /** Red unread-count pill (dbg-20260705). Rendered when > 0. */
  badgeCount?: number;
  testId?: string;
}

function NavLink({
  to,
  label,
  icon,
  active = false,
  disabled = false,
  badge,
  badgeCount,
  testId,
}: NavLinkProps): JSX.Element {
  const { collapsed } = useSidebar();
  const className = cn(
    'flex h-10 items-center gap-3 rounded-lg text-sm font-medium transition-colors',
    collapsed ? 'justify-center px-0' : 'px-3',
    active
      ? sidebarNavItemActiveClassName
      : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
    disabled && 'cursor-not-allowed opacity-60',
  );
  const inner = (
    <>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      {collapsed ? null : <span className="truncate">{label}</span>}
      {!collapsed && typeof badgeCount === 'number' && badgeCount > 0 ? (
        <span
          data-testid="sidebar-support-badge"
          className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white"
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : !collapsed && badge ? (
        <span className="ml-auto rounded-full border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-pw-text-muted">
          {badge}
        </span>
      ) : null}
    </>
  );
  // When collapsed, the label is hidden — surface it via the native title
  // tooltip so the icon-only rail stays discoverable.
  const title = collapsed ? label : undefined;
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        className={className}
        data-testid={testId}
        title={title}
      >
        {inner}
      </span>
    );
  }
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      className={className}
      data-testid={testId}
      title={title}
    >
      {inner}
    </Link>
  );
}

interface NavGroupProps {
  label: string;
  icon: JSX.Element;
  children: JSX.Element | JSX.Element[];
  /** Initial expanded state — caller sets true when a child route is active. */
  defaultOpen?: boolean;
}

// Chevron that points right (collapsed) and rotates to point down (expanded).
function GroupChevron({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        'h-4 w-4 shrink-0 transition-transform duration-150',
        open && 'rotate-90',
      )}
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

// NavGroup — a collapsible section header (accordion) with indented child
// links. Clicking the header toggles the sub-menu. Used for the Finance
// (Earnings/Payouts) and Dashboard (Analytics/SLA Reports) clusters.
function NavGroup({
  label,
  icon,
  children,
  defaultOpen = false,
}: NavGroupProps): JSX.Element {
  const { collapsed } = useSidebar();
  const [open, setOpen] = useState(defaultOpen);

  // Collapsed rail: drop the non-navigating header + indent guide and render
  // the child links flat (each child centers its own icon). A thin divider
  // keeps the cluster visually separated. No accordion when icon-only.
  if (collapsed) {
    return (
      <div className="flex flex-col gap-1">
        <div className="mx-2 my-1 border-t border-pw-border" />
        {children}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-semibold text-pw-text transition-colors hover:bg-pw-surface"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="truncate">{label}</span>
        <span className="ml-auto flex items-center text-pw-text-muted">
          <GroupChevron open={open} />
        </span>
      </button>
      {open ? (
        <div className="ml-4 flex flex-col gap-1 border-l border-pw-border pl-2">
          {children}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icon set — lucide-react would pull a full icon bundle for
// 9 glyphs, so we keep these as inline strokes (mirrors apps/admin Sidebar
// + apps/merchant Sidebar conventions).
// ---------------------------------------------------------------------------

const I = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  orders: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  packingGroups: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" /><path d="m7 16.5-4.74-2.85" /><path d="m7 16.5 5-3" /><path d="M7 16.5v5.17" /><path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" /><path d="m17 16.5-5-3" /><path d="m17 16.5 4.74-2.85" /><path d="M17 16.5v5.17" /><path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" /><path d="M12 8 7.26 5.15" /><path d="m12 8 4.74-2.85" /><path d="M12 13.5V8" />
    </svg>
  ),
  settingsExtended: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <line x1="21" y1="4" x2="14" y2="4" /><line x1="10" y1="4" x2="3" y2="4" /><line x1="21" y1="12" x2="12" y2="12" /><line x1="8" y1="12" x2="3" y2="12" /><line x1="21" y1="20" x2="16" y2="20" /><line x1="12" y1="20" x2="3" y2="20" /><line x1="14" y1="2" x2="14" y2="6" /><line x1="8" y1="10" x2="8" y2="14" /><line x1="16" y1="18" x2="16" y2="22" />
    </svg>
  ),
  products: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="m20.91 8.84-8.57 3.51a1 1 0 0 1-.76 0l-8.57-3.51a.5.5 0 0 1 0-.92l8.57-3.51a1 1 0 0 1 .76 0l8.57 3.51a.5.5 0 0 1 0 .92Z" /><path d="m3.09 8.84 8.57 3.51a1 1 0 0 0 .76 0l8.57-3.51" /><path d="M3.09 15.84 12 19.92l8.91-4.08" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  earnings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  payouts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  ),
  analytics: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  notifications: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  ),
  external: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  // vg-binding: P18.D-21 — the Integration entry. Same plug glyph the shared
  // SettingsLayout tab strip uses (lucide `Plug`), hand-inlined because this
  // file draws its own icons rather than importing a set.
  integration: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" /><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
    </svg>
  ),
  kyc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" />
    </svg>
  ),
  question: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  printQueue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  performance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
    </svg>
  ),
};

export function VendorSidebar(): JSX.Element {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation('common');
  // vg-binding: P18.D-21 — the Integration entry's label lives in the
  // `integration` namespace (the one P18.D-21 mandates in three locales), not in
  // `common`. Keeping it there means the page and its nav entry are translated
  // by the same catalog, so a locale can never have the page but not the link.
  const { t: tIntegration } = useTranslation('integration');
  const supportUnread = useVendorSupportUnread();

  return (
    <Sidebar header={<PrintwayLogo />}>
      <div className="flex flex-col gap-1">
        <NavGroup
          label={t('navigation.dashboardGroup')}
          icon={I.dashboard}
          defaultOpen={
            pathname.startsWith('/analytics') ||
            pathname.startsWith('/performance')
          }
        >
          <NavLink
            to="/analytics"
            label={t('navigation.analytics')}
            icon={I.analytics}
            testId="sidebar-analytics"
            active={pathname.startsWith('/analytics')}
          />
          <NavLink
            to="/performance"
            label={t('navigation.performance')}
            icon={I.performance}
            testId="sidebar-performance"
            active={pathname.startsWith('/performance')}
          />
        </NavGroup>

        <NavLink
          to="/orders"
          label={t('navigation.orders')}
          icon={I.orders}
          testId="sidebar-orders"
          active={pathname.startsWith('/orders')}
        />
        <NavLink
          to="/packing-groups"
          label={t('navigation.packingGroups')}
          icon={I.packingGroups}
          testId="sidebar-packing-groups"
          active={pathname.startsWith('/packing-groups')}
        />
        <NavLink
          to="/products"
          label={t('navigation.myProduct')}
          icon={I.products}
          testId="sidebar-products"
          active={pathname.startsWith('/products')}
        />

        <NavGroup
          label={t('navigation.finance')}
          icon={I.earnings}
          defaultOpen={
            pathname.startsWith('/earnings') ||
            pathname.startsWith('/payouts')
          }
        >
          <NavLink
            to="/earnings"
            label={t('navigation.earnings')}
            icon={I.earnings}
            testId="sidebar-earnings"
            active={pathname.startsWith('/earnings')}
          />
          <NavLink
            to="/payouts"
            label={t('navigation.payouts')}
            icon={I.payouts}
            testId="sidebar-payouts"
            active={pathname.startsWith('/payouts')}
          />
        </NavGroup>

        <NavLink
          to="/inventory"
          label={t('navigation.inventory')}
          icon={I.inventory}
          testId="sidebar-inventory"
          active={pathname.startsWith('/inventory')}
        />

        <div className="my-2 border-t border-pw-border" />

        <NavLink
          to="/print-queue"
          label={t('navigation.printQueue')}
          icon={I.printQueue}
          testId="sidebar-print-queue"
          active={pathname.startsWith('/print-queue')}
        />
        <NavLink
          to="/notifications"
          label={t('navigation.notifications')}
          icon={I.notifications}
          testId="sidebar-notifications"
          active={pathname.startsWith('/notifications')}
        />
        <NavLink
          to="/settings/kyc"
          label={t('navigation.kyc')}
          icon={I.kyc}
          testId="sidebar-kyc"
          active={pathname.startsWith('/settings/kyc')}
        />
        <NavLink
          to="/settings/profile"
          label={t('navigation.settings')}
          icon={I.settings}
          testId="sidebar-settings"
          active={
            (pathname === '/settings' || pathname.startsWith('/settings/')) &&
            !pathname.startsWith('/settings/kyc') &&
            // The Integration page has its own entry below; without this the
            // two rows would both read as active on `/settings/integration`.
            !pathname.startsWith('/settings/integration')
          }
        />
        {/* vg-binding: P18.D-21 — `/settings/integration` is deep-linkable and
            reachable from the rail, next to Settings. The vendor rail has no
            Settings accordion to nest into (Settings is a flat link here; the
            sub-navigation the mockup draws is the shared SettingsLayout tab
            strip, where task-09 already registered this tab). */}
        <NavLink
          to="/settings/integration"
          label={tIntegration('nav.tab')}
          icon={I.integration}
          testId="sidebar-integration"
          active={pathname.startsWith('/settings/integration')}
        />
        <NavLink
          to="/settings-extended"
          label={t('navigation.settingsExtended')}
          icon={I.settingsExtended}
          testId="sidebar-settings-extended"
          active={pathname.startsWith('/settings-extended')}
        />
        <NavLink
          to="/support"
          label={t('navigation.support')}
          icon={I.support}
          testId="sidebar-support"
          active={pathname.startsWith('/support')}
          badgeCount={supportUnread}
        />
      </div>
    </Sidebar>
  );
}

export default VendorSidebar;
